import { useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { useNavigate, useSearchParams } from "react-router-dom"
import { LayoutGrid, Loader2, Search, TriangleAlert, X } from "lucide-react"
import { useTenantFilter } from "@/hooks/useTenantFilter"
import { FilterBar } from "@/components/portal/FilterBar"
import { Dossier, type DossierDaten } from "@/components/search/Dossier"
import { EmailModal } from "@/components/portal/EmailModal"
import { GRAPH_FARBEN, KindIcon } from "@/components/search/RelationGraph"
import { letzteSucheLesen, letzteSucheMerken } from "@/lib/letzteSuche"

/**
 * /projekte — die Suche.
 *
 * Ein Feld fuer alles: Name, Firma, Rufnummer, Vorgangs-, Angebots- oder
 * Rechnungsnummer, Adresse, Stichwort aus einer Mail. Getroffen wird waehrend
 * des Tippens, weil alles aus Postgres kommt und kein HERO-Aufruf im Weg steht.
 *
 * Der eigentliche Anlass ist der eingehende Anruf: Nummer eintippen, waehrend
 * der Anrufer noch spricht. Darum springt die Ansicht bei einer Rufnummer mit
 * genau einem Treffer sofort ins Dossier — ein zusaetzlicher Klick waere in
 * dem Moment einer zu viel.
 */

type Treffer = {
  kind: string
  id: number
  title: string
  subtitle?: string
  phone?: string
  email?: string
  address?: string
  status?: string
  date?: string | null
  amount?: number | null
  type_name?: string
  direction?: string
  contact_id?: number | null
  project_id?: number | null
  project_nr?: string | null
  link_view?: string | null
  tenant_slug?: string | null
  from_hero?: boolean
}

type SuchAntwort = {
  query: string
  phone: string | null
  groups: Record<string, Treffer[]>
  total: number
  errors?: Record<string, string>
  degraded?: boolean
  hero_fallback?: boolean
}

const GRUPPEN: Array<{ id: string; label: string; kind: string }> = [
  { id: "contacts", label: "Personen & Firmen", kind: "contact" },
  { id: "projects", label: "Vorgänge", kind: "project" },
  { id: "documents", label: "Angebote & Rechnungen", kind: "document" },
  { id: "emails", label: "E-Mails", kind: "email" },
  { id: "calls", label: "Anrufe", kind: "call" },
  { id: "appointments", label: "Termine", kind: "appointment" },
  { id: "tasks", label: "Aufgaben", kind: "task" },
]

function euro(n?: number | null): string {
  if (n == null) return ""
  return n.toLocaleString("de-DE", { style: "currency", currency: "EUR" })
}

/** Was oeffnet ein Treffer? Personen und Vorgaenge fuehren ins Dossier,
 *  alles andere zeigt das Dossier seines Kontakts bzw. Vorgangs — denn genau
 *  darum geht es: von irgendeinem Fundstueck aus den ganzen Zusammenhang sehen.
 *
 *  null heisst: der Treffer haengt an nichts. Dann oeffnet der Aufrufer den
 *  Eintrag selbst (Popup bzw. Link) — eine Zeile, die auf einen Klick gar
 *  nichts tut, ist das Schlimmste. Genau das passierte bei Mails ohne
 *  verknuepften HERO-Kontakt (13.08.2026). */
function zielDesTreffers(t: Treffer): { contact_id?: number; project_id?: number } | null {
  if (t.kind === "contact") return { contact_id: t.id }
  if (t.kind === "project") return { project_id: t.id }
  if (t.contact_id) return { contact_id: t.contact_id }
  if (t.project_id) return { project_id: t.project_id }
  return null
}

export default function SearchPage() {
  const [tenantSlug, setTenantSlug] = useTenantFilter()
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  /** Bringt die Adresse nichts mit (Link „Suche" in der Seitenleiste), steht
   *  die letzte Suche dieses Tabs wieder da (#18). */
  const [gemerkt] = useState(() => {
    const vonAussen = ["q", "contact_id", "project_id", "phone"].some((k) => params.get(k))
    return vonAussen ? null : letzteSucheLesen()
  })
  const [q, setQ] = useState(params.get("q") || gemerkt?.q || "")
  /** Beim Tippen fallen die Direkteinstiegs-Parameter aus der Adresse — sonst
   *  wuerde ein alter `contact_id` die neue Auswahl wieder ueberschreiben. */
  const einstiegLoeschen = (p: URLSearchParams) => {
    p.delete("contact_id"); p.delete("project_id"); p.delete("phone")
    return p
  }
  const [debounced, setDebounced] = useState(q)
  const [auswahl, setAuswahl] = useState<{ contact_id?: number; project_id?: number; phone?: string } | null>(gemerkt?.auswahl ?? null)
  const [autoErledigt, setAutoErledigt] = useState("")
  /** Mail-Popup fuer Treffer, die an keinem Kontakt und keinem Vorgang haengen. */
  const [mailOffen, setMailOffen] = useState<number | null>(null)
  const eingabe = useRef<HTMLInputElement>(null)

  // 160 ms: kurz genug, dass es sich wie Tippen anfuehlt, lang genug, dass
  // nicht jeder Anschlag eine Abfrage ausloest.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 160)
    return () => clearTimeout(t)
  }, [q])

  useEffect(() => { eingabe.current?.focus() }, [])

  // Suchbegriff in der Adresse halten — damit ein Treffer teilbar ist und
  // ein Reload nicht bei null anfaengt.
  useEffect(() => {
    const p = new URLSearchParams(params)
    if (debounced.trim()) p.set("q", debounced.trim()); else p.delete("q")
    setParams(p, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced])

  useEffect(() => { letzteSucheMerken({ q: debounced, auswahl }) }, [debounced, auswahl])

  // Direkteinstieg von aussen — aus dem Anruf-Banner (`?phone=`) oder aus der
  // globalen Suche in der Kopfzeile (`?contact_id=` / `?project_id=`).
  useEffect(() => {
    const tel = params.get("phone")
    const kid = params.get("contact_id")
    const pid = params.get("project_id")
    if (kid) setAuswahl({ contact_id: Number(kid) })
    else if (pid) setAuswahl({ project_id: Number(pid) })
    else if (tel) { setAuswahl({ phone: tel }); setQ(tel) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get("contact_id"), params.get("project_id"), params.get("phone")])

  const aktiv = debounced.trim().length >= 2

  const suche = useQuery<SuchAntwort>({
    queryKey: ["suche", debounced, tenantSlug || ""],
    enabled: aktiv,
    // Ergebnisse kurz halten: waehrend eines Gespraechs tippt man hin und her.
    staleTime: 20_000,
    queryFn: async () => {
      const url = `/api/v1/search?q=${encodeURIComponent(debounced.trim())}`
        + (tenantSlug ? `&tenant=${encodeURIComponent(tenantSlug)}` : "")
      const r = await fetch(url, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
  })

  const letzte = useQuery<{ items: Treffer[] }>({
    queryKey: ["suche-letzte", tenantSlug || ""],
    enabled: !aktiv,
    staleTime: 60_000,
    queryFn: async () => {
      const url = `/api/v1/search/recent`
        + (tenantSlug ? `?tenant=${encodeURIComponent(tenantSlug)}` : "")
      const r = await fetch(url, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
  })

  // Rufnummer mit genau einem Kontakt-Treffer → direkt ins Dossier. Der
  // Merker verhindert, dass eine spaetere Handauswahl wieder ueberschrieben
  // wird, solange derselbe Suchbegriff steht.
  useEffect(() => {
    const d = suche.data
    if (!d?.phone || autoErledigt === d.query) return
    const kontakte = d.groups.contacts || []
    if (kontakte.length === 1) {
      setAuswahl({ contact_id: kontakte[0].id })
      setAutoErledigt(d.query)
    } else if (kontakte.length === 0 && d.total === 0) {
      // Unbekannte Nummer: das Dossier sagt das ausdruecklich und zeigt,
      // ob von hier schon einmal jemand angerufen hat.
      setAuswahl({ phone: d.query })
      setAutoErledigt(d.query)
    }
  }, [suche.data, autoErledigt])

  const dossier = useQuery<DossierDaten>({
    queryKey: ["dossier", auswahl, tenantSlug || ""],
    enabled: !!auswahl,
    queryFn: async () => {
      const p = new URLSearchParams()
      if (auswahl?.contact_id) p.set("contact_id", String(auswahl.contact_id))
      if (auswahl?.project_id) p.set("project_id", String(auswahl.project_id))
      if (auswahl?.phone) p.set("phone", auswahl.phone)
      if (tenantSlug) p.set("tenant", tenantSlug)
      const r = await fetch(`/api/v1/search/dossier?${p}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
  })

  /** Klick auf einen Treffer: ins Dossier, wenn er irgendwo haengt — sonst
   *  den Eintrag selbst oeffnen. */
  const trefferOeffnen = (t: Treffer) => {
    const ziel = zielDesTreffers(t)
    if (ziel) { setAuswahl(ziel); return }
    if (t.kind === "email") { setMailOffen(t.id); return }
    if (t.kind === "call") { navigate(`/sipgate/${t.id}`); return }
    if (t.kind === "document" && t.link_view) {
      const u = /^https?:\/\//.test(t.link_view)
        ? t.link_view
        : `https://login.hero-software.de${t.link_view.startsWith("/") ? "" : "/"}${t.link_view}`
      window.open(u, "_blank", "noreferrer")
    }
  }

  const gefundeneGruppen = useMemo(
    () => GRUPPEN.filter((g) => (suche.data?.groups?.[g.id] || []).length > 0),
    [suche.data],
  )

  // Lesbarer Mandantenname je Slug. Gleicher queryKey wie die FilterBar →
  // geteilter Cache, kein zusaetzlicher Request.
  const tenants = useQuery<{ items?: Array<{ slug: string; name: string }> }>({
    queryKey: ["tenants-dropdown"],
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const r = await fetch("/api/v1/tenants", { credentials: "include" })
      if (!r.ok) return { items: [] }
      return r.json()
    },
  })
  /** Badge nur, wenn ohne Mandantenfilter gesucht wird — sonst ist es Rauschen. */
  const mandantVon = (slug?: string | null): string | undefined => {
    if (tenantSlug || !slug) return undefined
    return tenants.data?.items?.find((t) => t.slug === slug)?.name || slug
  }

  return (
    <div style={{ padding: "22px 26px", maxWidth: 1560, margin: "0 auto" }}>
      {/* ── Kopf ── */}
      {/* Die Tenant-Auswahl steht bewusst NICHT oben rechts: dort sitzt bereits
          die globale Suchpille, und die FilterBar legte sich als schwebende
          Karte darueber. */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
        <Search style={{ width: 20, height: 20, color: "#0284c7" }} />
        <h1 style={{ fontSize: 21, fontWeight: 700, color: "#0f172a", margin: 0 }}>Suche</h1>
      </div>
      <div style={{
        display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
        margin: "6px 0 14px",
      }}>
        <p style={{ fontSize: 12.5, color: "#64748b", margin: 0 }}>
          Name, Firma, Rufnummer, Vorgangs- oder Rechnungsnummer, Adresse, Stichwort aus einer Mail —
          alles im selben Feld.
        </p>
        <div className="suche-filter" style={{ marginLeft: "auto" }}>
          {/* Die FilterBar ist als eigenstaendige Karte gebaut; hier steht sie
              in einer Zeile und wird darum flacher gemacht. */}
          <style>{`.suche-filter .pp-filter-bar {
            padding: 5px 12px; margin-bottom: 0; box-shadow: none;
          }`}</style>
          <FilterBar tenant={tenantSlug} onTenantChange={setTenantSlug} />
        </div>
      </div>

      {/* ── Suchfeld ── */}
      <div style={{ position: "relative", marginBottom: 16 }}>
        <Search style={{
          position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)",
          width: 18, height: 18, color: "#94a3b8", pointerEvents: "none",
        }} />
        <input
          ref={eingabe}
          value={q}
          onChange={(e) => {
            setQ(e.target.value)
            setAuswahl(null)
            setParams(einstiegLoeschen(new URLSearchParams(params)), { replace: true })
          }}
          onKeyDown={(e) => { if (e.key === "Escape") { setQ(""); setAuswahl(null) } }}
          placeholder="z. B. 0721 98618919, Müller, SAN-590, RE-687, Hauptstraße 12 …"
          style={{
            width: "100%", fontSize: 16, padding: "14px 44px 14px 46px",
            border: "1px solid #cbd5e1", borderRadius: 12, outline: "none",
            background: "#fff", color: "#0f172a", boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
          }}
        />
        {suche.isFetching && (
          <Loader2 style={{
            position: "absolute", right: 44, top: "50%", transform: "translateY(-50%)",
            width: 16, height: 16, color: "#94a3b8", animation: "spin 1s linear infinite",
          }} />
        )}
        {q && (
          <button
            onClick={() => { setQ(""); setAuswahl(null); eingabe.current?.focus() }}
            style={{
              position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)",
              border: "none", background: "transparent", cursor: "pointer", color: "#94a3b8",
              padding: 2, lineHeight: 0,
            }}
          >
            <X style={{ width: 16, height: 16 }} />
          </button>
        )}
        <style>{`@keyframes spin { to { transform: translateY(-50%) rotate(360deg) } }`}</style>
      </div>

      {/* Teilausfall ehrlich melden — eine Suche, die stumm nichts findet,
          ist schlimmer als eine, die ihre Luecke zugibt. */}
      {suche.data?.degraded && (
        <div style={{
          display: "flex", alignItems: "center", gap: 9, background: "#fffbeb",
          border: "1px solid #fde68a", borderRadius: 10, padding: "9px 13px",
          fontSize: 12.5, color: "#92400e", marginBottom: 14,
        }}>
          <TriangleAlert style={{ width: 15, height: 15, flexShrink: 0 }} />
          <span>
            Teilergebnis — nicht alle Bereiche konnten durchsucht werden
            ({Object.keys(suche.data.errors || {}).join(", ")}).
          </span>
        </div>
      )}

      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* ── Trefferliste ── */}
        <div style={{ flex: "0 0 380px", minWidth: 320, maxWidth: "100%" }}>
          {!aktiv && (
            <div>
              <div style={{
                fontSize: 12, fontWeight: 700, textTransform: "uppercase",
                letterSpacing: 0.4, color: "#94a3b8", margin: "0 0 8px 2px",
              }}>
                Zuletzt bewegt
              </div>
              <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
                {(letzte.data?.items || []).map((t) => (
                  <TrefferZeile key={`r${t.id}`} t={t}
                    aktiv={auswahl?.project_id === t.id}
                    mandant={mandantVon(t.tenant_slug)}
                    onClick={() => setAuswahl({ project_id: t.id })} />
                ))}
                {!letzte.isLoading && !(letzte.data?.items || []).length && (
                  <div style={{ padding: 26, textAlign: "center", color: "#94a3b8", fontSize: 12.5 }}>
                    Noch keine Vorgänge im Cache.
                  </div>
                )}
              </div>
            </div>
          )}

          {aktiv && suche.data && (
            <>
              <div style={{
                fontSize: 12, fontWeight: 700, textTransform: "uppercase",
                letterSpacing: 0.4, color: "#94a3b8", margin: "0 0 8px 2px",
                display: "flex", justifyContent: "space-between",
              }}>
                <span>{suche.data.total} Treffer</span>
                {suche.data.hero_fallback && <span style={{ color: "#0284c7" }}>live aus HERO</span>}
              </div>

              {gefundeneGruppen.length === 0 && !suche.isFetching && (
                <div style={{
                  background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12,
                  padding: 28, textAlign: "center", color: "#64748b", fontSize: 13,
                }}>
                  Nichts gefunden zu <strong>{debounced}</strong>.
                  <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 6 }}>
                    Bei einer Rufnummer genügen die letzten Ziffern.
                  </div>
                </div>
              )}

              {gefundeneGruppen.map((g) => (
                <div key={g.id} style={{ marginBottom: 12 }}>
                  <div style={{
                    display: "flex", alignItems: "center", gap: 6, margin: "0 0 6px 2px",
                    fontSize: 12, fontWeight: 700, color: GRAPH_FARBEN[g.kind as never] || "#475569",
                  }}>
                    <KindIcon kind={g.kind as never} size={12} />
                    {g.label}
                    <span style={{ color: "#94a3b8", fontWeight: 600 }}>
                      {(suche.data!.groups[g.id] || []).length}
                    </span>
                  </div>
                  <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden" }}>
                    {(suche.data!.groups[g.id] || []).map((t) => {
                      const ziel = zielDesTreffers(t)
                      const ist = !!ziel && (
                        (ziel.contact_id != null && auswahl?.contact_id === ziel.contact_id) ||
                        (ziel.project_id != null && auswahl?.project_id === ziel.project_id)
                      )
                      return (
                        <TrefferZeile
                          key={`${t.kind}${t.id}`}
                          t={t}
                          aktiv={ist}
                          mandant={mandantVon(t.tenant_slug)}
                          onClick={() => trefferOeffnen(t)}
                        />
                      )
                    })}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* ── Dossier ── */}
        <div style={{ flex: "1 1 560px", minWidth: 340 }}>
          {!auswahl && (
            <div style={{
              background: "#fff", border: "1px dashed #cbd5e1", borderRadius: 12,
              padding: "56px 26px", textAlign: "center", color: "#94a3b8",
            }}>
              <LayoutGrid style={{ width: 30, height: 30, margin: "0 auto 12px", color: "#cbd5e1" }} />
              <div style={{ fontSize: 14, fontWeight: 600, color: "#64748b" }}>
                Treffer auswählen
              </div>
              <div style={{ fontSize: 12.5, marginTop: 5, lineHeight: 1.6 }}>
                Dann steht hier alles: Vorgänge, Korrespondenz, Anrufe, Belege,<br />
                Termine, Aufgaben — und wie das zusammenhängt.
              </div>
            </div>
          )}
          {auswahl && dossier.isLoading && (
            <div style={{
              background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12,
              padding: 48, textAlign: "center", color: "#94a3b8", fontSize: 13,
            }}>
              <Loader2 style={{ width: 18, height: 18, animation: "spin2 1s linear infinite" }} />
              <style>{`@keyframes spin2 { to { transform: rotate(360deg) } }`}</style>
            </div>
          )}
          {auswahl && dossier.error && (
            <div style={{
              background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12,
              padding: 20, color: "#991b1b", fontSize: 13,
            }}>
              Dossier konnte nicht geladen werden: {String(dossier.error)}
            </div>
          )}
          {auswahl && dossier.data && (
            <Dossier
              daten={dossier.data}
              onOeffneKontakt={(id) => setAuswahl({ contact_id: id })}
            />
          )}
        </div>
      </div>

      {/* Mails ohne HERO-Verknuepfung haben kein Dossier — die oeffnen direkt. */}
      <EmailModal
        emailId={mailOffen}
        open={mailOffen != null}
        onClose={() => setMailOffen(null)}
      />
    </div>
  )
}

function TrefferZeile({ t, aktiv, onClick, mandant }: {
  t: Treffer; aktiv: boolean; onClick: () => void
  /** Nur gesetzt, wenn OHNE Mandantenfilter gesucht wird. */
  mandant?: string
}) {
  const zusatz = [
    t.subtitle,
    t.kind === "contact" ? t.phone : null,
    t.kind === "document" ? euro(t.amount) : null,
    t.kind === "project" ? t.status : null,
    t.address,
  ].filter(Boolean).join(" · ")

  return (
    <div
      onClick={onClick}
      style={{
        display: "flex", alignItems: "flex-start", gap: 9, padding: "9px 12px",
        borderBottom: "1px solid #f1f5f9", cursor: "pointer",
        background: aktiv ? "#f0f9ff" : "transparent",
        borderLeft: `3px solid ${aktiv ? GRAPH_FARBEN[t.kind as never] || "#0284c7" : "transparent"}`,
      }}
    >
      <div style={{ marginTop: 2 }}><KindIcon kind={t.kind as never} /></div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{
          fontSize: 13, fontWeight: 600, color: "#0f172a",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {t.title}
          {/* Ohne Mandantenfilter laufen die Treffer ueber ALLE Mandanten.
              Dann muss an jeder Zeile stehen, zu wem sie gehoert — dasselbe
              Argument wie beim Anruf-Banner: wer alle Mandanten betreut, darf
              das nicht raten muessen. */}
          {mandant && (
            <span style={{
              fontSize: 12, fontWeight: 600, color: "#475569", background: "#f1f5f9",
              border: "1px solid #e2e8f0", borderRadius: 5, padding: "1px 6px",
              marginLeft: 6, verticalAlign: "middle",
            }}>
              {mandant}
            </span>
          )}
          {t.from_hero && (
            <span style={{
              fontSize: 12, fontWeight: 700, color: "#0284c7", background: "#e0f2fe",
              borderRadius: 4, padding: "1px 5px", marginLeft: 6, verticalAlign: "middle",
            }}>
              NEU IN HERO
            </span>
          )}
        </div>
        {zusatz && (
          <div style={{
            fontSize: 12, color: "#64748b", marginTop: 1,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {zusatz}
          </div>
        )}
      </div>
    </div>
  )
}
